<template>
  <div class="scheduled-appointments">
    <div class="appointments-container">
      <v-skeleton-loader
        v-if="loading"
        type="table-heading"
        :loading="loading" />

      <div v-if="!loading" class="appointments-content">
        <!-- Upcoming Appointments Section -->
        <div class="appointments-section">
          <h1 class="section-title">Upcoming Appointments</h1>
          
          <div v-if="upcomingAppointments.length" class="appointments-table">
            <div class="table-header">
              <div class="header-cell patient-col">Patient</div>
              <div class="header-cell provider-col">Provider</div>
              <div class="header-cell datetime-col">Date & Time</div>
            </div>
            
            <div class="table-body">
              <div 
                v-for="appointment in upcomingAppointments" 
                :key="appointment.aptNum"
                class="appointment-row">
                <div class="appointment-cell patient-col">
                  <div class="patient-info">
                    <div class="patient-avatar">
                      {{ getPatientInitials(appointment.patientName) }}
                    </div>
                    <div class="patient-name">{{ appointment.patientName }}</div>
                  </div>
                </div>
                <div class="appointment-cell provider-col">
                  <div class="provider-name">{{ appointment.providerName }}</div>
                </div>
                <div class="appointment-cell datetime-col">
                  <div class="datetime-info">
                    <div class="date-text">{{ formatDate(appointment.rawDate) }}</div>
                    <div class="time-text">{{ formatTime(appointment.rawDate) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <div class="empty-state-icon">
              <v-icon size="48" color="#9ca3af">mdi-calendar-plus</v-icon>
            </div>
            <h3 class="empty-state-title">No Upcoming Appointments</h3>
            <p class="empty-state-message">
              You don't have any upcoming appointments scheduled. Click the + button to schedule a new appointment.
            </p>
          </div>
        </div>

        <!-- Past Appointments Section -->
        <div class="appointments-section">
          <h1 class="section-title">Past Appointments</h1>
          
          <div v-if="pastAppointments.length" class="appointments-table">
            <div class="table-header">
              <div class="header-cell patient-col">Patient</div>
              <div class="header-cell provider-col">Provider</div>
              <div class="header-cell datetime-col">Date & Time</div>
            </div>
            
            <div class="table-body">
              <div 
                v-for="appointment in pastAppointments" 
                :key="appointment.aptNum"
                class="appointment-row">
                <div class="appointment-cell patient-col">
                  <div class="patient-info">
                    <div class="patient-avatar">
                      {{ getPatientInitials(appointment.patientName) }}
                    </div>
                    <div class="patient-name">{{ appointment.patientName }}</div>
                  </div>
                </div>
                <div class="appointment-cell provider-col">
                  <div class="provider-name">{{ appointment.providerName }}</div>
                </div>
                <div class="appointment-cell datetime-col">
                  <div class="datetime-info">
                    <div class="date-text">{{ formatDate(appointment.rawDate) }}</div>
                    <div class="time-text">{{ formatTime(appointment.rawDate) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="empty-state">
            <div class="empty-state-icon">
              <v-icon size="48" color="#9ca3af">mdi-calendar-check</v-icon>
            </div>
            <h3 class="empty-state-title">No Past Appointments</h3>
            <p class="empty-state-message">
              Your appointment history will appear here once you've had appointments.
            </p>
          </div>
        </div>
      </div>

      <!-- Add New Appointment FAB -->
      <div class="fab-container">
        <v-btn
          color="appPrimaryBtnColor"
          class="add-appointment-fab"
          dark
          elevation="6"
          fab
          @click="$router.push('/patient_schedule')">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import DataService from '../../../_services/DataService';
import Utils from '../../../src/utils.js';

export default {
  name: 'ScheduledAppointments',
  data() {
    return {
      loading: true,
      pageName: 'Appointments',
      pageIcon: 'mdi-calendar-month',
      form: {},
      upcomingAppointments: [],
      pastAppointments: [],
      expanded: [],
      headers: [
        {
          text: 'Patient',
          value: 'patientName',
          sortable: false,
          width: '300px',
        },
        {
          text: 'Provider',
          value: 'providerName',
          sortable: false,
          width: '300px',
        },
        {
          text: 'Date & Time',
          value: 'dateAndTime',
          sortable: false,
        },
      ],
    };
  },
  methods: {
    async getScheduleData() {
      this.loading = true;
      const apptResponse = await DataService.getPatientAppointments();
      if (apptResponse.status === 200) {
        const mappedData = apptResponse.data.map(this.getDisplayData);

        const now = new Date();
        this.upcomingAppointments = [];
        this.pastAppointments = [];
        for (const row of mappedData) {
          if (new Date(row.rawDate) > now) {
            this.upcomingAppointments.push(row);
          } else {
            this.pastAppointments.push(row);
          }
        }

        // upcoming; earliest first
        this.upcomingAppointments.sort((a, b) => a.rawDate > b.rawDate ? 1 : -1);
        // past; most recent first
        this.pastAppointments.sort((a, b) => a.rawDate > b.rawDate ? -1 : 1);

        setTimeout(() => (this.loading = false), 100);
      }
    },
    getDisplayData(d) {
      return {
        rawDate: d.date,
        dateAndTime: `${Utils.formatDate(d.date)}\u00A0${Utils.formatTime(d.date)}`,
        patientName: `${d.patient.firstName} ${d.patient.lastName}`,
        providerName: `${d.provider.firstName} ${d.provider.lastName}`,
        aptNum: d.aptNum || Math.random(), // Ensure unique key
      };
    },
    getPatientInitials(fullName) {
      const names = fullName.split(' ');
      return names.length >= 2 ? names[0][0] + names[names.length - 1][0] : names[0][0];
    },
    formatDate(dateString) {
      return Utils.formatDate(dateString);
    },
    formatTime(dateString) {
      return Utils.formatTime(dateString);
    },
  },
  async mounted() {
    await this.getScheduleData();
  },
};
</script>
<style lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

// Component-specific color variables
$appointments-bg-color: white;
$appointments-border-color: #e2e8f0;
$section-title-color: #1f2937;
$table-header-bg-color: #f8fafc;
$table-header-text-color: #374151;
$appointment-row-bg-color: white;
$appointment-row-hover-color: #f8fafc;
$appointment-row-border-color: #f1f5f9;
$patient-avatar-bg-color: #133b54;
$patient-avatar-text-color: white;
$patient-name-color: #374151;
$provider-name-color: #6b7280;
$date-text-color: #374151;
$time-text-color: #6b7280;
$fab-bg-color: #b0d35e;
$fab-shadow-color: rgba(176, 211, 94, 0.3);
$empty-state-bg-color: #f9fafb;
$empty-state-border-color: #e5e7eb;
$empty-state-text-color: #6b7280;

.scheduled-appointments {
  @include mobile-nav-spacing;
  
  .appointments-container {
    padding: 16px;
  }
  
  .appointments-content {
    display: flex;
    flex-direction: column;
    gap: 40px;
    
    @media (max-width: $breakpoint-mobile) {
      gap: 32px;
    }
  }
  
  .appointments-section {
    background: $appointments-bg-color;
    border: 1px solid $appointments-border-color;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: $shadow-sm;
    
    .section-title {
      font-size: 24px;
      font-weight: 700;
      color: $section-title-color;
      margin: 0;
      padding: 24px 24px 20px 24px;
      border-bottom: 1px solid $appointments-border-color;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 20px;
        padding: 20px 16px 16px 16px;
      }
    }
  }
  
  .appointments-table {
    .table-header {
      display: grid;
      grid-template-columns: 2fr 2fr 1.5fr;
      background: $table-header-bg-color;
      border-bottom: 1px solid $appointments-border-color;
      
      @media (max-width: $breakpoint-mobile) {
        grid-template-columns: 1fr;
        display: none; // Hide headers on mobile for cleaner look
      }
      
      .header-cell {
        padding: 16px 24px;
        font-size: 14px;
        font-weight: 600;
        color: $table-header-text-color;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        
        @media (max-width: $breakpoint-mobile) {
          padding: 12px 16px;
          font-size: 12px;
        }
      }
    }
    
    .table-body {
      .appointment-row {
        display: grid;
        grid-template-columns: 2fr 2fr 1.5fr;
        border-bottom: 1px solid $appointment-row-border-color;
        transition: background-color $transition-fast ease;
        
        @media (max-width: $breakpoint-mobile) {
          grid-template-columns: 1fr;
          gap: 12px;
          padding: 16px;
        }
        
        &:hover {
          background: $appointment-row-hover-color;
        }
        
        &:last-child {
          border-bottom: none;
        }
        
        .appointment-cell {
          padding: 20px 24px;
          display: flex;
          align-items: center;
          
          @media (max-width: $breakpoint-mobile) {
            padding: 0;
          }
          
          &.patient-col {
            .patient-info {
              display: flex;
              align-items: center;
              gap: 12px;
              
              .patient-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                background: $patient-avatar-bg-color;
                color: $patient-avatar-text-color;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 600;
                font-size: 14px;
                flex-shrink: 0;
                
                @media (max-width: $breakpoint-mobile) {
                  width: 36px;
                  height: 36px;
                  font-size: 12px;
                }
              }
              
              .patient-name {
                font-size: 16px;
                font-weight: 600;
                color: $patient-name-color;
                
                @media (max-width: $breakpoint-mobile) {
                  font-size: 14px;
                }
              }
            }
          }
          
          &.provider-col {
            .provider-name {
              font-size: 15px;
              font-weight: 500;
              color: $provider-name-color;
              
              @media (max-width: $breakpoint-mobile) {
                font-size: 14px;
                font-weight: 600;
                color: $patient-name-color;
              }
            }
            
            @media (max-width: $breakpoint-mobile) {
              &::before {
                content: 'Provider: ';
                font-weight: 600;
                color: $table-header-text-color;
                margin-right: 8px;
              }
            }
          }
          
          &.datetime-col {
            .datetime-info {
              display: flex;
              flex-direction: column;
              gap: 2px;
              
              .date-text {
                font-size: 15px;
                font-weight: 600;
                color: $date-text-color;
                
                @media (max-width: $breakpoint-mobile) {
                  font-size: 14px;
                }
              }
              
              .time-text {
                font-size: 13px;
                font-weight: 500;
                color: $time-text-color;
                
                @media (max-width: $breakpoint-mobile) {
                  font-size: 12px;
                }
              }
            }
            
            @media (max-width: $breakpoint-mobile) {
              &::before {
                content: 'Date & Time: ';
                font-weight: 600;
                color: $table-header-text-color;
                margin-right: 8px;
              }
              
              .datetime-info {
                flex-direction: row;
                gap: 8px;
                align-items: center;
              }
            }
          }
        }
      }
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 60px 24px;
    
    @media (max-width: $breakpoint-mobile) {
      padding: 40px 16px;
    }
    
    .empty-state-icon {
      margin-bottom: 16px;
    }
    
    .empty-state-title {
      font-size: 18px;
      font-weight: 600;
      color: $section-title-color;
      margin: 0 0 12px 0;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 16px;
      }
    }
    
    .empty-state-message {
      font-size: 14px;
      color: $empty-state-text-color;
      line-height: 1.5;
      margin: 0;
      max-width: 400px;
      margin-left: auto;
      margin-right: auto;
      
      @media (max-width: $breakpoint-mobile) {
        font-size: 13px;
      }
    }
  }
  
  .fab-container {
    position: fixed;
    bottom: 90px; // Above mobile nav
    right: 24px;
    z-index: 10;
    
    @media (max-width: $breakpoint-mobile) {
      bottom: 90px; // Keep above mobile nav on mobile
      right: 20px;
    }
    
    .add-appointment-fab {
      background: $fab-bg-color !important;
      box-shadow: 0 4px 12px $fab-shadow-color !important;
      transition: all $transition-fast ease;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px $fab-shadow-color !important;
      }
      
      .v-icon {
        font-size: 24px !important;
      }
    }
  }
}
</style>
